import React, { useEffect, useState } from 'react';
import style from 'styled-components';
import { Palette } from 'core/config';
import PlacesAutocomplete from 'react-places-autocomplete';
import { FormControl, Input, FormHelperText } from '@material-ui/core';
import { Data } from 'core/utilities';
import { getIOSInputEventHandlers } from 'view/hooks';

export const getAddressField = (components : any, field : any, useShortName = false) => 
{
    const result = components.find((component : any) => component.types.includes(field));
    if (useShortName) 
    {
        return !!result && result.short_name !== undefined ? result.short_name : undefined;
    }
    return !!result && result.long_name !== undefined ? result.long_name : undefined;
};

export const getStreetAddress = (components : any) => 
{
    let number = '';
    let street = '';

    components.some((component : any) => 
    {
        if (number === undefined) 
        {
            if (component.types.includes('street_number')) 
            {
                number = component.long_name;
            }
        }
        if (street === undefined) 
        {
            if (component.types.includes('route')) 
            {
                street = component.long_name;
            }
        }
        return number !== undefined && street !== undefined;
    });

    return `${number ? number : ''} ${street ? street : ''}`.trim();
};

export const getPlaceDetails = (placeId : string) : Promise<google.maps.places.PlaceResult | null> => 
{
    const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));

    return new Promise(resolve => 
    {
        placesService.getDetails({ placeId }, result => 
        {
            resolve(result);
        });
    });
};

interface Props 
{
    label? : string;
    initialValues? : { [key : string] : any };
    placeholder? : string;
    setValue : (name : string, value : any) => void;
    setFormErrors? : React.Dispatch<React.SetStateAction<Data>>;
    inputs : {
        full_address : any;
        address : any;
        city    : any;
        region  : any;
        country : any;
        postal  : any;
    };
}

export const LocationInput : React.FunctionComponent<Props> = ({ 
    label,
    placeholder,
    initialValues,
    inputs,
    setValue,
    setFormErrors
}) => 
{
    const [ fullAddress, setFullAddress ] = useState<string>(initialValues?.full_address ?? '');
    const [ selectedAddress, setSelectedAddress ] = useState<string>(initialValues?.full_address ?? '');
    const [ error, setError ] = useState<string>('');

    useEffect(() => 
    {
        if (initialValues)
        {
            const {
                address,
                city,
                region,
                country,
                postal
            } = initialValues;

            address && setValue('address', address);
            city    && setValue('city', city);
            region  && setValue('region', region);
            country && setValue('country', country);
            postal  && setValue('postal', postal);
        }
    }, []);

    const handleChange = (fullAddress : string) => 
    {       
        setFullAddress(fullAddress);

        if (!fullAddress)
        {
            setValue('full_address', fullAddress);
            setSelectedAddress('');
            setError('');
        }

    };

    const handleSelect = (fullAddress : string, placeId : string) => 
    {
        if (placeId) 
        {
            setFullAddress(fullAddress);
            getPlaceDetails(placeId).then((place : any) => 
            {
                if (place.address_components) 
                {
                    const address = {
                        address  : getStreetAddress(place.address_components),
                        streetNumber: getAddressField(place.address_components, 'street_number'),
                        route: getAddressField(place.address_components, 'route'),
                        city     : getAddressField(place.address_components, 'locality'),
                        province : getAddressField(place.address_components, 'administrative_area_level_1'),
                        country  : getAddressField(place.address_components, 'country', true),
                        postal   : getAddressField(place.address_components, 'postal_code')
                    };

                    let addressString = place.name ? place.name : '';
                    if (!addressString) 
                    {
                        addressString = address.address ? address.address : '';
                    }

                    // PORT-651: require street number and name
                    if (!address.streetNumber || !address.route)
                    {
                        const errorMsg =  'Street number and name required';
                        setFullAddress('');
                        setValue('full_address', '');
                        setFormErrors?.(errors => ({
                            ...errors,
                            full_address : errorMsg
                        }));
                        setError(errorMsg);
                        return;
                    }

                    setSelectedAddress(fullAddress);
                    setValue('full_address', fullAddress);
                    setValue('address', addressString);
                    setValue('city', address.city);
                    setValue('region', address.province);
                    setValue('country', address.country);
                    setValue('postal', address.postal);

                    if (!fullAddress)
                    {
                        const errorMsg = 'Address is required';
                        setFormErrors?.(errors => ({
                            ...errors,
                            full_address : errorMsg
                        }));
                        setError(errorMsg);
                    }
                    setError('');
                } 
                else 
                {
                    setSelectedAddress(fullAddress);
                    setValue('full_address', fullAddress);
                }
            });
        }
    };

    const handleBlur = () =>
    {
        if (fullAddress !== selectedAddress)
        {
            setFullAddress(selectedAddress);
        }
    };

    return (
        <Container>
            {label && <Label>{label}</Label>}
            <PlacesAutocomplete value={fullAddress} onChange={handleChange} onSelect={handleSelect}>
                {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => 
                {
                    return (
                        <React.Fragment>
                            <LocationContainer margin='normal'>
                                <InputContainer
                                    id='location-search-input'
                                    placeholder={placeholder ?? ''}
                                    {...getInputProps({
                                        className: 'location-search-input',
                                        fullWidth: true,
                                        name: 'address-autocomplete',
                                        onBlur: handleBlur,
                                    })}
                                    {...getIOSInputEventHandlers()}
                                />
                                <FormHelperText error={Boolean(error)}>
                                    {error}
                                </FormHelperText>

                            </LocationContainer>
                            {(loading || !suggestions.length) ? null : (
                                <DropdownContainer>
                                    {suggestions.map(suggestion => (
                                        <DropdownItem {...getSuggestionItemProps(suggestion)} key={suggestion.placeId}>
                                            <span>{suggestion.description}</span>
                                        </DropdownItem>
                                    ))}
                                </DropdownContainer>
                            )}
                        </React.Fragment>
                    );
                }}
            </PlacesAutocomplete>
            <HiddenInput name='fullAddress' {...inputs.full_address} />
            <HiddenInput name='address' {...inputs.address} />
            <HiddenInput name='city'    {...inputs.city} />
            <HiddenInput name='region'  {...inputs.region} />
            <HiddenInput name='country' {...inputs.country} />
            <HiddenInput name='postal'  {...inputs.postal} />
        </Container>
    );
};

const HiddenInput = style.input`
    visibility: hidden;
    position: absolute;
`;

const Container = style.div`
    margin-left: 60px;
    margin-right: 60px;
    margin-bottom: -7px;
`;

const Label = style.div`
    margin-top: 20px;
    color: ${Palette.BLUE};
    font-size: 10px;
    font-family: 'SofiaPro-Regular';

    @media only screen and (min-width: 1300px) {
        font-size: 12px;
    }

    @media only screen and (min-width: 1900px) {
        font-size: 14px;
        white-space: nowrap;
    }
`;

const InputContainer = style(Input)`
    background-color: ${Palette.EGGSHELL_WHITE};
    border-radius: 11px;
    height: 37px;
    padding-left: 15px;
    padding-right: 15px;
    margin-top: -10px;
    z-index: 11;
`;

const DropdownContainer = style.div`
    position: absolute;
    z-index: 10;
    width: 299px;
    max-height: 244px;
    border-radius: 11px;
    color: ${Palette.BLUE};
    font-size: 12px;
    font-family: 'SofiaPro-Regular';
    margin-top: -15px;
    box-shadow: 0px 5px 5px -3px rgba(0,0,0,0.2),0px 8px 10px 1px rgba(0,0,0,0.14),0px 3px 14px 2px rgba(0,0,0,0.12);

    @media only screen and (min-width: 1900px) {
        font-size: 14px;
    }
`;

const DropdownItem = style.div`
    cursor: pointer;
    background-color: ${Palette.EGGSHELL_WHITE};
    padding: 15px 10px 15px 10px;

    &:hover {
        background-color: ${Palette.TAN};
    }
`;

const LocationContainer = style(FormControl)`
    .MuiInput-underline::after {
        border: none;
    }

    .MuiInput-underline::before {
        border: none;
        content: none;
    }

    .MuiInputBase-root {
        color: ${Palette.BLUE};
        font-size: 12px;
        font-family: 'SofiaPro-Regular';
        width: 299px;

        @media only screen and (min-width: 1900px) {
            font-size: 14px;
        }
    }
    
    .MuiInputBase-input::placeholder {
        opacity: 0.2;
    }

    .MuiFormHelperText-root {
        font-size: 8px;
        font-family: 'SofiaPro-Regular';

        @media only screen and (min-width: 1300px) {
            font-size: 10px;
        }

        @media only screen and (min-width: 1900px) {
            font-size: 12px;
            white-space: nowrap;
        }
    }
`;